// Nome a exibir pra fornecedor: prefere nome_fantasia, fallback razao_social.
export function getNomeFornecedor(f: { nome_fantasia?: string | null; razao_social?: string | null } | null | undefined): string {
  if (!f) return '—';
  const nf = (f.nome_fantasia || '').trim();
  const rs = (f.razao_social || '').trim();
  return nf || rs || '—';
}

// O codigo do fornecedor tem DUAS formas, e confundi-las foi o defeito da DA-125 (ver DA-471):
//
//   o que se GRAVA   -> o codigo do FORNECEDOR, como ele o escreve: "0600300020" (FORCE-LINE),
//                       "0037.516.314" (ORCA). Sai impresso no pedido de compra que vai pra ele.
//                       E o `limparCodigoFornecedor` abaixo, que so tira folga de digitacao.
//   o que se COMPARA -> a forma canonica, que casa e trava duplicata ("0006381" e "6381" sao a
//                       MESMA SKU). Vive SO no banco — ver o bloco no fim deste arquivo.
//
// Ate 2026-08-07 a canonica era tambem a gravada, entao o banco guardava um codigo que o
// fornecedor nao usa — e a busca, que nunca normalizou o termo digitado, nao achava o item pelo
// codigo verdadeiro.

/** O que se GRAVA: o codigo do fornecedor como ele o escreve. So tira folga de digitacao. */
export function limparCodigoFornecedor(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).trim().toUpperCase();
}

// A forma CANONICA (a que compara e trava duplicata) NAO se calcula aqui — ela tem uma definicao
// so, e ela mora no banco: a funcao `norm_codigo_fornecedor(text)` (migration 0198). As colunas
// geradas `*_norm` a chamam, e toda consulta normaliza o parametro por ela.
//
// Ate a 0198 havia uma versao em TS espelhando a expressao SQL — duas copias que so um humano
// atento manteria iguais, e o CLAUDE.md ja registra que "lembre de atualizar os dois lugares"
// falhou por definicao. Se precisar da canonica no TS, o certo e PERGUNTAR ao banco
// (`SELECT norm_codigo_fornecedor($1)`), nunca reimplementar.

/**
 * O CONTRATO da forma canonica — nao a implementacao dela.
 *
 * Nao ha aqui nada que CALCULE a canonica (era esse o problema). Isto e a lista de casos que a
 * funcao do banco tem que satisfazer, e quem cobra e o detector do `deploy.js`, que roda estes
 * pares contra o Postgres de verdade a cada deploy. Serve pra duas coisas que uma copia em TS nao
 * daria: descreve a regra em linguagem de exemplo, e sobrevive a um `CREATE OR REPLACE` na funcao
 * (que o Postgres deixa passar calado, deixando as colunas geradas com a regra velha).
 *
 * Caso novo aqui = caso a mais cobrado no proximo deploy.
 */
export const CASOS_NORM_CODIGO_FORNECEDOR: ReadonlyArray<{ entrada: string; esperado: string }> = [
  // as duas grafias do MESMO codigo tem que dar na mesma chave (o bug que originou a DA-471)
  { entrada: '0600300020', esperado: '600300020' },   // FORCE-LINE, como ela escreve
  { entrada: '600300020', esperado: '600300020' },    // como o sistema guardava antes
  { entrada: '0006381', esperado: '6381' },           // o caso que a DA-125 queria colapsar
  { entrada: '6381', esperado: '6381' },
  { entrada: '005407', esperado: '5407' },            // TAMBASA, dois zeros
  { entrada: '000483', esperado: '483' },             // tres zeros
  // separador: a DA-125 registrou que "nenhum caso real tem separador" e a ORCA e o caso
  { entrada: '0037.516.314', esperado: '37516314' },
  { entrada: '37516314', esperado: '37516314' },
  { entrada: '412520-11', esperado: '41252011' },
  { entrada: '  6381  ', esperado: '6381' },
  // caixa: nao ha minuscula na base hoje, e a chave nao pode nascer torta quando houver
  { entrada: 'br12a', esperado: 'BR12A' },
  // "0" puro sobrevive — uma reimplementacao ingenua (LTRIM de zeros) devolveria vazio
  { entrada: '0', esperado: '0' },
  { entrada: '000', esperado: '0' },
  // so separador nao tem chave: e o que faz o codigo nao servir de identidade
  { entrada: '---', esperado: '' },
  { entrada: '   ', esperado: '' },
];

// Raiz do CNPJ = 8 primeiros digitos (identifica a EMPRESA; filiais compartilham a raiz).
// Ex: Martins GO 43214055004013 e Martins MG 43214055000107 -> ambas raiz 43214055.
// CPF (11 digitos) ou doc curto -> retorna os digitos como estao (sem raiz de empresa).
// Vazio/nulo -> ''. Espelhada no SQL por LEFT(regexp_replace(cpf_cnpj,'\D','','g'), 8).
export function raizCnpj(cpfCnpj: string | null | undefined): string {
  if (cpfCnpj == null) return '';
  const digitos = String(cpfCnpj).replace(/\D/g, '');
  if (!digitos) return '';
  // CNPJ tem 14 digitos; a raiz sao os 8 primeiros. Docs menores ficam inteiros.
  return digitos.length >= 8 ? digitos.slice(0, 8) : digitos;
}
